import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSignupWizard } from './signup-wizard-context';
import WizardStep from './wizard-step';
import { useStepNavigation } from './use-step-navigation';
import { ServiceSupplierSignup, Category } from '../../services/service-supplier-signup';
import Selector, { SelectorType } from '../../components/shared/Selector/Selector';

const StepCategories: FC = () => {
  const { t } = useTranslation('newSupplier', { keyPrefix: 'wizard' });
  const { state } = useSignupWizard();

  const [categories, setCategories] = useState<Category[]>([]);
  const [topCategories, setTopCategories] = useState<{ areaId: string; name: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>(state.areaIds || []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cats, top] = await Promise.all([
          ServiceSupplierSignup.getCategories(),
          ServiceSupplierSignup.getTopCategories(6).catch(() => []),
        ]);
        setCategories(cats);
        setTopCategories(Array.isArray(top) ? top : []);
      } catch {
        // silently ignore
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const allOptions: SelectorType[] = categories
    .map(c => ({ value: c._id, label: c.name }));

  const selectedCategories: SelectorType[] = categories
    .filter(cat => selectedIds.includes(cat._id))
    .map(c => ({ value: c._id, label: c.name }));

  // Top categories ranked by procurement activity, filtered to exclude already-selected
  const suggestions = topCategories.length > 0
    ? topCategories.filter(tc => !selectedIds.includes(tc.areaId))
    : categories.filter(cat => !selectedIds.includes(cat._id)).slice(0, 6);

  const toggleCategory = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const { handleNext, handleBack } = useStepNavigation({
    currentStep: 2,
    nextPath: '/supplier-sign-up/places',
    prevPath: '/supplier-sign-up',
    validate: () => selectedIds.length > 0,
    onBeforeNext: () => ({ areaIds: selectedIds }),
    eventName: 'signup_wizard_step_categories',
  });

  const canProceed = selectedIds.length > 0;

  return (
    <WizardStep
      step={2}
      title={t('categories.title', 'Inom vilka yrkeskategorier verkar ert företag?')}
      description={t('categories.description', 'Yrkeskategorier')}
      onNext={handleNext}
      onBack={handleBack}
      canProceed={canProceed}
    >
      {/* Category selector */}
      <Selector<SelectorType, true>
        isMulti
        isSearchable
        options={allOptions}
        value={selectedCategories}
        onChange={(values) => {
          const ids = [...values].map(v => v!.value);
          setSelectedIds(ids);
        }}
        placeholder={t('categories.searchPlaceholder', 'Sök yrkeskategorier...')}
        isDisabled={loading}
      />

      {/* Suggestion chips */}
      {suggestions.length > 0 && (
        <div>
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>{t('categories.popular', 'Populära kategorier')}</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map(cat => {
              const id = 'areaId' in cat ? cat.areaId : cat._id;
              const name = 'areaName' in cat ? cat.areaName : cat.name;
              return (
              <button
                key={id}
                type="button"
                onClick={() => toggleCategory(id)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  border: '1px solid var(--gray-300)',
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ height: '0.875rem', width: '0.875rem' }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                {name}
              </button>
              );
            })}
          </div>
        </div>
      )}
    </WizardStep>
  );
};

export default StepCategories;
